import i18next from "i18next";
import catalog from "./klem-module-catalog.json";

// ── Tipler ──────────────────────────────────────────────────────

export type ConfiguratorModuleKind = string;

type Source =
  | { type: "attribute"; key: string }
  | { type: "material"; part: "fabric" | "color" | "full" };

type Entry = {
  id: string;
  label: string;
  kind: string;
  attributes?: Record<string, string | Source[]>;
};

/** Sahne modellerinden sadece kullandığımız alanların yapısal tipi. */
interface SceneConfiguratorPart {
  name?: string;
}

interface SceneConfigurator {
  mainConfig?: Map<string, SceneConfiguratorPart>;
  sideConfig?: Map<string, SceneConfiguratorPart>;
}

interface SceneModel {
  userData?: { info?: { id?: number }; [key: string]: unknown };
  data?: { id?: number };
  configurator?: SceneConfigurator;
  partMaterialGroups?: ReadonlyArray<{ material?: { name?: string } }>;
}

export type ResolvedModuleWithFeatures = {
  id: string;
  key: string;
  label: string;
  kind: string;
  groupIndex: number;
  module: string;
  fabric: string;
  color: string;
  summary: string;
  features: Record<string, string>;
};

// ── Katalog yükleme ─────────────────────────────────────────────

const FIELDS = catalog.displayFields as Record<string, Record<string, Source[]>>;
const ALIASES = (catalog.optionNameAliases ?? {}) as Record<string, string>;
const KINDS = new Set(Object.keys(FIELDS));
const canon = (s: string) => ALIASES[s] ?? s;

const RESOLVE: Record<string, Entry> = {};
for (const [key, row] of Object.entries(catalog.resolve)) {
  if (row.id && row.label && row.kind && KINDS.has(row.kind)) {
    RESOLVE[key] = row as Entry;
  }
}

const KEYS = Object.keys(RESOLVE);
const PAIR_KEYS = KEYS.filter((k) => /^\d+\.\d+$/.test(k));

const LEG_IDS = new Set<number>();
for (const k of KEYS) {
  const m = k.match(/^(\d+)#/);
  if (m) LEG_IDS.add(+m[1]);
}

// ── Materyal yardımcıları ───────────────────────────────────────

function splitMaterial(name: string) {
  const s = name.trim();
  if (!s) return { fabric: "", color: "" };
  const i = s.indexOf("-");
  if (i < 0) return { fabric: "", color: s };
  return { fabric: s.slice(0, i).trim(), color: s.slice(i + 1).trim() };
}

function getModelMaterial(model: SceneModel): string {
  for (const g of model.partMaterialGroups ?? []) {
    if (g?.material?.name) return String(g.material.name).trim();
  }
  return "";
}

function getModelId(m: SceneModel): number | undefined {
  const id = m.userData?.info?.id ?? m.data?.id;
  return typeof id === "number" ? id : undefined;
}

// ── Sahne → variant attrs ───────────────────────────────────────

function extractSceneAttrs(group: SceneModel[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const node of group) {
    const cfg = node.configurator;
    if (!cfg) continue;

    if (cfg.mainConfig?.get) {
      const leg = cfg.mainConfig.get("legIn");
      const back = cfg.mainConfig.get("backOut");
      if (leg?.name && !out.legType) out.legType = String(leg.name).trim();
      if (back?.name && !out.backCushion) out.backCushion = String(back.name).trim();
    }

    if (!out.armType && cfg.sideConfig?.size) {
      const byKey = cfg.sideConfig.get("side");
      const part = byKey?.name?.trim()
        ? byKey
        : Array.from(cfg.sideConfig.values()).find((p) => p?.name?.trim());
      if (part?.name) out.armType = String(part.name).trim();
    }
  }
  return out;
}

// ── Display options oluşturma ───────────────────────────────────

function buildDisplayOptions(
  entry: Entry,
  materialName: string,
  sceneCtx: Record<string, string>,
) {
  const ctx = { ...sceneCtx };
  if (entry.attributes) {
    for (const [k, v] of Object.entries(entry.attributes)) {
      if (typeof v === "string" && !ctx[k]) ctx[k] = v;
    }
  }

  const { fabric, color } = splitMaterial(materialName);
  const materialPart = (p: string) =>
    p === "fabric" ? fabric : p === "color" ? (color || materialName.trim()) : materialName.trim();

  const seen = new Set<string>();
  const out: { name: string; value: string }[] = [];

  const tryField = (field: string, sources: Source[]) => {
    const c = canon(field);
    if (seen.has(c)) return;
    seen.add(c);
    for (const src of sources) {
      const v = src.type === "attribute" ? (ctx[src.key] ?? "").trim() : materialPart(src.part);
      if (v) {
        out.push({ name: c, value: v });
        return;
      }
    }
  };

  for (const [field, sources] of Object.entries(FIELDS[entry.kind] ?? {})) {
    tryField(field, sources);
  }
  if (entry.attributes) {
    for (const [field, val] of Object.entries(entry.attributes)) {
      if (Array.isArray(val)) tryField(field, val);
    }
  }

  return out;
}

// ── Sahne pool eşleme ───────────────────────────────────────────

type PoolEntry = { id: number; model: SceneModel };

function takeFromPool(pool: PoolEntry[], id: number): PoolEntry | undefined {
  const i = pool.findIndex((e) => e.id === id);
  return i < 0 ? undefined : pool.splice(i, 1)[0];
}

function countInPool(pool: PoolEntry[], id: number): number {
  return pool.filter((e) => e.id === id).length;
}

function buildResolvedRow(key: string, entry: Entry, groupIndex: number, materialName: string) {
  const { fabric, color } = splitMaterial(materialName);
  const module = String(i18next.t(entry.label, { defaultValue: entry.label }));
  const summary = [module, fabric, color].filter(Boolean).join(" ");
  return { id: entry.id, key, label: entry.label, kind: entry.kind, groupIndex, module, fabric, color, summary: summary || module };
}

function matchModelsFromPool(pool: PoolEntry[], groupIndex: number) {
  const out: ReturnType<typeof buildResolvedRow>[] = [];

  for (const key of PAIR_KEYS) {
    const [, a, b] = key.match(/^(\d+)\.(\d+)$/)!;
    const entry = RESOLVE[key];
    if (!entry) continue;
    while (countInPool(pool, +a) > 0 && countInPool(pool, +b) > 0) {
      const ea = takeFromPool(pool, +a);
      const eb = takeFromPool(pool, +b);
      if (!ea || !eb) break;
      out.push(buildResolvedRow(key, entry, groupIndex, getModelMaterial(ea.model) || getModelMaterial(eb.model)));
    }
  }

  for (const meshId of LEG_IDS) {
    const count = countInPool(pool, meshId);
    if (count <= 0) continue;
    const entry = RESOLVE[`${meshId}#${count}`];
    if (!entry) continue;
    let firstModel: SceneModel | undefined;
    for (let i = 0; i < count; i++) {
      const taken = takeFromPool(pool, meshId);
      if (taken && !firstModel) firstModel = taken.model;
    }
    out.push(buildResolvedRow(`${meshId}#${count}`, entry, groupIndex, firstModel ? getModelMaterial(firstModel) : ""));
  }

  while (pool.length) {
    const { id } = pool[0];
    const entry = RESOLVE[String(id)];
    if (!entry) { pool.shift(); continue; }
    const taken = takeFromPool(pool, id);
    if (!taken) break;
    out.push(buildResolvedRow(String(id), entry, groupIndex, getModelMaterial(taken.model)));
  }

  return out;
}

// ── Public API ──────────────────────────────────────────────────

export function resolveSceneModulesWithFeatures(products: unknown): ResolvedModuleWithFeatures[] {
  if (!Array.isArray(products)) return [];

  const out: ResolvedModuleWithFeatures[] = [];

  products.forEach((group: unknown, groupIndex: number) => {
    if (!Array.isArray(group) || !group.length) return;
    const first = group[0];
    if (!first || typeof first !== "object" || !("userData" in first || "data" in first)) return;

    const models = group as SceneModel[];
    const pool: PoolEntry[] = [];
    for (const m of models) {
      const id = getModelId(m);
      if (id !== undefined) pool.push({ id, model: m });
    }

    for (const resolved of matchModelsFromPool(pool, groupIndex)) {
      const materialLine = resolved.fabric && resolved.color
        ? `${resolved.fabric} - ${resolved.color}`
        : resolved.fabric || resolved.color || "";
      const ctx = extractSceneAttrs(models);
      const options = buildDisplayOptions(RESOLVE[resolved.key], materialLine, ctx);
      const features = Object.fromEntries(options.map((o) => [o.name, o.value]));
      out.push({ ...resolved, features });
    }
  });

  return out;
}
